import React, { useState, useEffect, useRef, useMemo } from 'react';
import MaterialTable from 'material-table';
import TableIcons from '../components/TableIcons';
import { tableOptions } from '../components/TableDefaults';
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import moment from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

export default function Promos() {
  const { t,i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    editPromo
  } = api;
  const settings = useSelector(state => state.settingsdata.settings);

  const inputRef = useRef(null);

  const columns = [
    { title: t('promo_name'), field: 'promo_name',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LocalOfferIcon sx={{ color: '#6366F1', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
              {rowData.promo_name || '-'}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
              {rowData.promo_discount_type === 'percentage' ? `${rowData.promo_discount_value}% off` : `${settings.symbol || '$'}${rowData.promo_discount_value} off`}
            </Typography>
          </Box>
        </Box>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('description'), field: 'promo_description',
      render: rowData => (
        <Typography sx={{ fontSize: 14, color: '#374151', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {rowData.promo_description || '-'}
        </Typography>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    {
      title: t('type'),
      field: 'promo_discount_type',
      lookup: { flat: t('flat'), percentage: t('percentage')},
      render: rowData => (
        <Chip
          label={rowData.promo_discount_type === 'percentage' ? (t('percentage') || 'Percentage') : (t('flat') || 'Flat')}
          size="small"
          sx={{
            bgcolor: rowData.promo_discount_type === 'percentage' ? '#EEF2FF' : '#F0FDF4',
            color: rowData.promo_discount_type === 'percentage' ? '#4F46E5' : '#16A34A',
            fontWeight: 500,
            fontSize: 12,
          }}
        />
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('promo_discount_value'), field: 'promo_discount_value', type: 'numeric',
      render: rowData => (
        <Typography sx={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>
          {rowData.promo_discount_type === 'percentage' ? `${rowData.promo_discount_value}%` : `${settings.symbol || '$'}${rowData.promo_discount_value}`}
        </Typography>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('max_limit'), field: 'max_promo_discount_value', type: 'numeric',
      render: rowData => (
        <Typography sx={{ fontSize: 14, color: '#374151' }}>
          {settings.symbol || '$'}{rowData.max_promo_discount_value || 0}
        </Typography>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('min_limit'), field: 'min_order', type: 'numeric',
      render: rowData => (
        <Typography sx={{ fontSize: 14, color: '#374151' }}>
          {settings.symbol || '$'}{rowData.min_order || 0}
        </Typography>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('end_date'), field: 'promo_validity',
      editComponent: props => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            inputRef={inputRef}
            renderInput={(props) => <TextField {...props} />}
            label="DateTimePicker"
            value={props.rowData.promo_validity ? props.rowData.promo_validity : new Date()}
            onChange={(newValue) => {
              props.onChange(newValue)
            }}
            autoFocus={false}
          />
        </LocalizationProvider>
      ),
      render: rowData => (
        <Typography sx={{ fontSize: 14, color: '#374151' }}>
          {rowData.promo_validity ? moment(rowData.promo_validity).format('lll') : '-'}
        </Typography>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('promo_usage'), field: 'promo_usage_limit', type: 'numeric',
      render: rowData => (
        <Typography sx={{ fontSize: 14, color: '#374151' }}>
          {rowData.promo_usage_limit || 0}
        </Typography>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('promo_used_by'), field: 'user_avail', editable: 'never',
      render: rowData => (
        <Chip
          label={rowData.user_avail || 0}
          size="small"
          sx={{ bgcolor: '#F3F4F6', color: '#374151', fontWeight: 500, fontSize: 12 }}
        />
      ),
      cellStyle: { padding: '14px 16px' },
    }
  ];

  const [data, setData] = useState([]);
  const promodata = useSelector(state => state.promodata);
  const dispatch = useDispatch();

  useEffect(() => {
    if (promodata.promos) {
      setData(promodata.promos);
    } else {
      setData([]);
    }
  }, [promodata.promos]);

  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [typeAnchor, setTypeAnchor] = useState(null);

  // Filtered data
  const filteredData = useMemo(() => {
    let result = data;
    if (filterType !== 'all') {
      result = result.filter(d => d.promo_discount_type === filterType);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(d =>
        (d.promo_name && d.promo_name.toLowerCase().includes(query)) ||
        (d.promo_description && d.promo_description.toLowerCase().includes(query))
      );
    }
    return result;
  }, [data, filterType, searchQuery]);

  const clearFilters = () => {
    setFilterType('all');
    setSearchQuery('');
  };

  return (
    promodata.loading ? <CircularLoading /> :
    <div>
      {/* Uber-Style Filter Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          p: 2,
          mb: 2,
          bgcolor: '#FFFFFF',
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          alignItems: 'center',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <TextField
          size="small"
          placeholder={t('search') || 'Search promos...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            minWidth: 220,
            '& .MuiOutlinedInput-root': {
              borderRadius: 50,
              bgcolor: '#F7F7F7',
              '& fieldset': { border: 'none' },
              '&:hover': { bgcolor: '#EFEFEF' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#6B7280', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ width: 1, height: 24, bgcolor: '#E5E7EB', mx: 1 }} />
        <Chip
          label={t('all') || 'All'}
          onClick={clearFilters}
          sx={{
            bgcolor: filterType === 'all' ? '#111827' : '#F7F7F7',
            color: filterType === 'all' ? '#FFFFFF' : '#374151',
            fontWeight: 500,
            fontSize: 13,
            height: 36,
            borderRadius: 50,
            px: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: filterType === 'all' ? '#000000' : '#EFEFEF' },
          }}
        />
        <Chip
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {filterType === 'all' ? (t('type') || 'Type') : filterType === 'percentage' ? (t('percentage') || 'Percentage') : (t('flat') || 'Flat')}
              <ExpandMoreIcon sx={{ fontSize: 16, ml: 0.5 }} />
            </Box>
          }
          onClick={(e) => setTypeAnchor(e.currentTarget)}
          sx={{
            bgcolor: filterType !== 'all' ? '#111827' : '#F7F7F7',
            color: filterType !== 'all' ? '#FFFFFF' : '#374151',
            fontWeight: 500,
            fontSize: 13,
            height: 36,
            borderRadius: 50,
            px: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: filterType !== 'all' ? '#000000' : '#EFEFEF' },
          }}
        />
        <Menu
          anchorEl={typeAnchor}
          open={Boolean(typeAnchor)}
          onClose={() => setTypeAnchor(null)}
          PaperProps={{ sx: { borderRadius: 2, mt: 1, minWidth: 150, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } }}
        >
          <MenuItem onClick={() => { setFilterType('all'); setTypeAnchor(null); }}>
            <Typography sx={{ fontSize: 14 }}>{t('all') || 'All'}</Typography>
          </MenuItem>
          <MenuItem onClick={() => { setFilterType('percentage'); setTypeAnchor(null); }}>
            <Typography sx={{ fontSize: 14 }}>{t('percentage') || 'Percentage'}</Typography>
          </MenuItem>
          <MenuItem onClick={() => { setFilterType('flat'); setTypeAnchor(null); }}>
            <Typography sx={{ fontSize: 14 }}>{t('flat') || 'Flat'}</Typography>
          </MenuItem>
        </Menu>
        <Typography sx={{ ml: 'auto', fontSize: 13, color: '#6B7280' }}>
          {filteredData.length} {t('promo_offer') || 'promos'}
        </Typography>
      </Box>

      <MaterialTable
      icons={TableIcons}
      title={null}
      columns={columns}
      style={{
        direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
        borderRadius: 12,
        boxShadow: 'none',
        border: '1px solid #E5E7EB',
        overflow: 'hidden',
      }}
      data={filteredData}
      onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
      options={{
        ...tableOptions,
        search: false,
        toolbar: false,
        showTitle: false,
        rowStyle: rowData => ({
          backgroundColor: (selectedRow === rowData.tableData.id) ? '#F3F4F6' : '#fff'
        }),
      }}
      localization={{body:{
        addTooltip: (t('add')),
        deleteTooltip: (t('delete')),
        editTooltip: (t('edit')),
        emptyDataSourceMessage: (
          (t('blank_message'))
      ),
      editRow: { 
        deleteText: (t('delete_message')),
        cancelTooltip: (t('cancel')),
        saveTooltip: (t('save')) 
        }, 
        },
        toolbar: {
          searchPlaceholder: (t('search')),
          exportTitle: (t('export')),
        },
        header: {
          actions: (t('actions')) 
      },
      pagination: {
        labelDisplayedRows: ('{from}-{to} '+ (t('of'))+ ' {count}'),
        firstTooltip: (t('first_page_tooltip')),
        previousTooltip: (t('previous_page_tooltip')),
        nextTooltip: (t('next_page_tooltip')),
        lastTooltip: (t('last_page_tooltip'))
      },
      }}
      editable={{
        onRowAdd: newData =>
          new Promise((resolve, reject) => {
            setTimeout(() => {
              if(!(newData.promo_name &&  newData.promo_description && newData.promo_discount_value && newData.promo_discount_type && newData.promo_discount_value && newData.max_promo_discount_value && newData.min_order && newData.promo_usage_limit && (newData.min_order >= newData.max_promo_discount_value) && (newData.promo_discount_value>=newData.max_promo_discount_value))){
                alert(t('no_details_error'));
                reject();
              }else{
                newData['createdAt'] = new Date().getTime();
                newData['promo_validity'] = newData.promo_validity? new Date(newData.promo_validity).getTime() : null;
                dispatch(editPromo(newData,"Add"));
                resolve();
              }
            }, 600);
          }),
        onRowUpdate: (newData, oldData) =>
          settings.AllowCriticalEditsAdmin?
          new Promise((resolve, reject) => {
            setTimeout(() => {
              if(!(newData.promo_name &&  newData.promo_description && newData.promo_discount_value && newData.promo_discount_type && newData.promo_discount_value && newData.max_promo_discount_value && newData.min_order && newData.promo_usage_limit && (newData.min_order >= newData.max_promo_discount_value) && (newData.promo_discount_value>=newData.max_promo_discount_value))){
                alert(t('no_details_error'));
                reject();
              }else{
                resolve();
                newData['promo_validity'] = newData.promo_validity? new Date(newData.promo_validity).getTime(): null;
                if(newData !== oldData){
                  delete newData.tableData;
                  dispatch(editPromo(newData,"Update"));
                }
              }
            }, 600);
          })
          :
          new Promise(resolve => {
            setTimeout(() => {
              resolve();
              alert(t('demo_mode'));
            }, 600);
          }),
        onRowDelete: oldData =>
          settings.AllowCriticalEditsAdmin?
          new Promise(resolve => {
            setTimeout(() => {
              resolve();
              dispatch(editPromo(oldData,"Delete"));
            }, 600);
          })
          :
          new Promise(resolve => {
            setTimeout(() => {
              resolve();
              alert(t('demo_mode'));
            }, 600);
          })
      }}
    />
    </div>
  );
}
